import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MessageSquare, Music2 } from "lucide-react";
import ChatUI from "@/components/ChatUI";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TIKTOK_URL = "https://www.tiktok.com/@nick_stath0";

const Chat = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [threadId, setThreadId] = useState(params.get("thread_id") || null);
  const [buyerName, setBuyerName] = useState("");
  const [step, setStep] = useState(threadId ? "chat" : "name"); // name | chat | error
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!sessionId) {
      setStep("error");
      setErrorMsg("Missing session. Please go through checkout first.");
    }
  }, [sessionId]);

  // Try to restore thread_id from localStorage if not in URL
  useEffect(() => {
    if (!threadId && sessionId) {
      const saved = localStorage.getItem(`gg_thread_${sessionId}`);
      const savedName = localStorage.getItem(`gg_name_${sessionId}`);
      if (saved) {
        setThreadId(saved);
        if (savedName) setBuyerName(savedName);
        setStep("chat");
      }
    }
  }, [sessionId, threadId]);

  const handleStart = async (e) => {
    e?.preventDefault();
    const name = buyerName.trim();
    if (!name) return;
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await axios.post(`${API}/chat/access`, {
        session_id: sessionId,
        buyer_name: name,
      });
      const tid = res.data.thread_id;
      localStorage.setItem(`gg_thread_${sessionId}`, tid);
      localStorage.setItem(`gg_name_${sessionId}`, name);
      setThreadId(tid);
      setStep("chat");
      // keep URL clean / shareable
      navigate(`/chat?session_id=${sessionId}&thread_id=${tid}`, { replace: true });
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail || "Could not start chat";
      setErrorMsg(detail);
      if (err?.response?.status === 403) {
        setStep("error");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (after) => {
    const url = `${API}/chat/${threadId}/messages?session_id=${encodeURIComponent(
      sessionId
    )}${after ? `&after=${encodeURIComponent(after)}` : ""}`;
    const res = await axios.get(url);
    return res.data;
  };

  const sendMessage = async ({ text, attachments }) => {
    const url = `${API}/chat/${threadId}/messages?session_id=${encodeURIComponent(
      sessionId
    )}`;
    const res = await axios.post(url, { text, attachments });
    return res.data;
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-[#1a1a1a] px-4 sm:px-6 py-3 flex items-center justify-between bg-[#050505] sticky top-0 z-20">
        <Link
          to="/"
          className="flex items-center gap-2 text-[#a1a1aa] hover:text-yellow transition"
          data-testid="chat-home-link"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="font-display text-lg tracking-wide uppercase">
            Greek God <span className="text-yellow">Physique</span>
          </span>
        </Link>
        <a
          href={TIKTOK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717a] hover:text-yellow flex items-center gap-2"
        >
          <Music2 className="h-3 w-3" /> @nick_stath0
        </a>
      </header>

      {/* Content */}
      <div className="flex-1 px-4 sm:px-6 py-6 lg:py-10 max-w-4xl w-full mx-auto flex flex-col">
        {step === "name" && (
          <div className="max-w-md w-full mx-auto mt-10" data-testid="step-name">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow mb-3">
              Private Chat
            </div>
            <h1 className="font-display text-5xl sm:text-6xl leading-[0.9]">
              ONE LAST <span className="text-yellow">STEP</span>.
            </h1>
            <p className="text-[#d4d4d8] mt-5 leading-relaxed">
              Drop your first name and I'll open a private chat with you. I'll
              send the Starter Blueprint directly inside it.
            </p>

            <form onSubmit={handleStart} className="mt-8 space-y-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717a] block mb-2">
                  Your first name
                </label>
                <input
                  type="text"
                  value={buyerName}
                  onChange={(e) => setBuyerName(e.target.value)}
                  required
                  maxLength={60}
                  placeholder="e.g. Alex"
                  autoFocus
                  data-testid="chat-name-input"
                  className="w-full bg-[#0a0a0a] border border-[#262626] focus:border-yellow text-white px-4 py-3 outline-none font-sans"
                />
              </div>

              {errorMsg && (
                <div className="text-red-400 text-sm font-mono" data-testid="chat-error">
                  {errorMsg}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !buyerName.trim()}
                className="btn-brutal w-full justify-center disabled:opacity-50"
                data-testid="chat-start-btn"
              >
                <MessageSquare className="h-5 w-5" />
                {loading ? "Opening Chat…" : "Open Chat"}
              </button>
            </form>
          </div>
        )}

        {step === "chat" && threadId && (
          <div className="h-[78vh] sm:h-[80vh] flex" data-testid="step-chat">
            <ChatUI
              threadId={threadId}
              currentSide="buyer"
              title={`Chat with Nick`}
              subtitle={buyerName ? `You: ${buyerName}` : "Greek God Physique System"}
              fetchMessages={fetchMessages}
              sendMessage={sendMessage}
            />
          </div>
        )}

        {step === "error" && (
          <div className="max-w-md mx-auto mt-10 text-center" data-testid="step-error">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow">
              Access Issue
            </div>
            <h1 className="font-display text-5xl sm:text-6xl mt-3">
              CAN'T <span className="text-yellow">OPEN</span> CHAT.
            </h1>
            <p className="text-[#a1a1aa] mt-5">
              {errorMsg ||
                "You need a completed purchase to access the private chat."}
            </p>
            <Link
              to="/"
              className="btn-brutal mt-8 inline-flex"
              data-testid="chat-back-home"
            >
              <ArrowLeft className="h-5 w-5" /> Back to Blueprint
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chat;
